<template>
  <div ref="rootEl" class="rounded-xl border"
    :class="[highlight ? 'versions-highlight' : '',
             disabled ? 'bg-gray-50 dark:bg-[#161b22] border-gray-100 dark:border-gray-800' : 'bg-white dark:bg-[#1C2128] border-gray-100 dark:border-gray-700']">
    <div class="flex items-center justify-between px-4 py-2.5 border-b"
      :class="disabled ? 'border-gray-100 dark:border-gray-800' : 'border-gray-100 dark:border-gray-700'">
      <p class="text-xs font-semibold flex items-center gap-1.5" :class="disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'">
        <!-- Lock cue so this whole card reads as "unlocks once you save",
             not just a header sitting above grayed-out fields. -->
        <svg v-if="disabled" class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
        </svg>
        Versions
        <span v-if="!disabled && !loading" class="font-normal text-gray-400 dark:text-gray-500">({{ versions.length }})</span>
        <span v-if="highlight" class="ml-1.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400">New — add one now</span>
      </p>
      <button v-if="!disabled && !formOpen" type="button" @click="openForm"
        class="flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
        Add
      </button>
    </div>

    <div class="p-3 space-y-2.5">
      <!-- Disabled preview: the form shape renders inert and blurred behind a
           centered "locked" message, rather than relying on muted colors
           alone to read as disabled — a clearer, more common "unlocks once
           you save" pattern (like a blurred/paywalled preview). -->
      <div v-if="disabled" class="relative">
        <div class="blur-[3px] opacity-70 pointer-events-none select-none" aria-hidden="true">
          <OrgComponentVersionForm disabled :component-name="componentName" :component-logo-url="componentLogoUrl" />
        </div>
        <div class="absolute inset-0 flex items-center justify-center px-6">
          <div class="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">
            <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"/>
            </svg>
            <p class="text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">Add the IT component first to track versions</p>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-else-if="loading" class="animate-pulse space-y-1.5">
        <div v-for="i in skeletonCount" :key="i" class="h-9 bg-gray-100 dark:bg-gray-800 rounded"></div>
      </div>

      <template v-else>
        <OrgComponentVersionForm v-if="formOpen && !editingId" :organization-id="organizationId" :global-component-id="globalComponentId"
          :component-name="componentName" :component-logo-url="componentLogoUrl" :existing-versions="existingVersionStrings"
          :saving="saving" :error="formError" submit-label="Add version"
          @cancel="closeForm" @submit="submitVersion" />

        <div v-if="!versions.length && !formOpen" class="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/40 rounded-lg">
          <svg class="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <div>
            <p class="text-xs font-semibold text-amber-700 dark:text-amber-400">No versions tracked yet</p>
            <p class="text-xs text-amber-600 dark:text-amber-500">Add one to use this component elsewhere in the app.</p>
          </div>
        </div>

        <div v-if="versions.length" class="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-50/70 dark:bg-black/10 border-b border-gray-100 dark:border-gray-700">
                <th v-for="col in COLUMNS" :key="col.field" @click="col.sortable && toggleSort(col.field)"
                  class="px-3 py-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider whitespace-nowrap transition-colors"
                  :class="[col.align === 'right' ? 'text-right' : '', col.sortable ? 'cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-300' : '']">
                  <span class="inline-flex items-center gap-1" :class="col.align === 'right' ? 'flex-row-reverse' : ''">
                    {{ col.label }}
                    <svg v-if="col.sortable" class="w-2.5 h-2.5 flex-shrink-0" :class="sortBy === col.field ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600'"
                      fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                      <path v-if="sortBy === col.field && sortOrder === 'asc'" stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5"/>
                      <path v-else stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <template v-for="v in sortedVersions" :key="v.id">
                <!-- Editing this row: the form replaces it entirely, spanning every column. -->
                <tr v-if="formOpen && editingId === v.id">
                  <td colspan="5" class="p-2">
                    <OrgComponentVersionForm :organization-id="organizationId" :global-component-id="globalComponentId"
                      :component-name="componentName" :component-logo-url="componentLogoUrl" :existing-versions="existingVersionStrings"
                      :initial-version="v.version" :initial-alias="v.alias || ''"
                      :initial-released-date="v.released_date" :initial-eol-date="v.eol_date"
                      :initial-eol-not-announced="v.eol_date_not_announced" :auto-focus="false"
                      :saving="saving" :error="formError" submit-label="Save version"
                      @cancel="closeForm" @submit="submitVersion" />
                  </td>
                </tr>
                <tr v-else class="group/version hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors" :class="versionRowStatusBg(v)">
                  <td class="px-3 py-2.5">
                    <div class="flex items-center gap-1.5">
                      <ItComponentLogo v-if="componentName" :name="componentName" :logo-url="componentLogoUrl" size="w-5 h-5" initial-text-size="text-[8px]" class="flex-shrink-0" />
                      <span class="text-xs font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{{ v.version }}</span>
                      <span v-if="v.id === currentVersionId" class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">Active</span>
                    </div>
                  </td>
                  <td class="px-3 py-2.5 text-xs whitespace-nowrap" :class="v.alias ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                    {{ v.alias || 'Not specified' }}
                  </td>
                  <td class="px-3 py-2.5 text-xs whitespace-nowrap" :class="v.released_date ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                    {{ v.released_date ? formatDate(v.released_date) : 'Not specified' }}
                  </td>
                  <td class="px-3 py-2.5">
                    <div class="flex items-center gap-1.5">
                      <span class="text-xs whitespace-nowrap" :class="v.eol_date ? 'text-gray-600 dark:text-gray-300' : 'text-gray-300 dark:text-gray-600 italic'">
                        {{ v.eol_date ? formatDate(v.eol_date) : (v.eol_date_not_announced ? 'Not announced' : 'Not specified') }}
                      </span>
                      <span v-if="eolStatus(v.eol_date) !== 'none'"
                        class="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold cursor-default" :class="VERSION_EOL_BADGE_CLASSES[eolStatus(v.eol_date)]"
                        :title="eolStatusMessage(v.eol_date)">
                        {{ VERSION_EOL_BADGE_LABELS[eolStatus(v.eol_date)] }}
                      </span>
                    </div>
                  </td>
                  <td class="px-3 py-2.5">
                    <div class="flex items-center justify-end gap-1.5 opacity-0 group-hover/version:opacity-100 transition-opacity">
                      <button type="button" @click="openEditForm(v)"
                        class="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-md cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-200 dark:hover:border-blue-800 transition-colors">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                        </svg>
                        Edit
                      </button>
                      <button type="button" @click="requestRemove(v)" :disabled="!canDelete(v)" :title="deleteDisabledReason(v)"
                        class="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-md cursor-pointer hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400 disabled:hover:border-gray-200 dark:disabled:hover:border-gray-700">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>

    <ConfirmModal :open="pendingRemove.open" title="Remove this version?"
      message="This organization's record of running this version will be removed. You can add it again later if needed."
      confirm-text="Delete" :saving="pendingRemove.saving" @cancel="cancelRemove" @confirm="confirmRemove">
      <template v-if="pendingRemove.version" #subject>
        <span class="text-sm font-medium text-gray-800 dark:text-gray-100">{{ pendingRemove.version.version }}</span>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useOrgComponentVersionsStore } from 'stores/orgComponentVersions.js'
import { getCachedOrgComponentVersionsCount } from 'composables/useOrgComponentVersionsCountCache.js'
import OrgComponentVersionForm from 'controllers/vue_components/organizations/it_components/OrgComponentVersionForm.vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'
import { eolStatus, VERSION_EOL_BADGE_CLASSES, VERSION_EOL_BADGE_LABELS } from 'controllers/vue_components/organizations/applications/versionEolStatus.js'

const props = defineProps({
  organizationId: { type: String, required: true },
  // Empty until the parent OrgComponent has actually been saved — see `disabled`.
  orgComponentId: { type: String, default: '' },
  globalComponentId: { type: String, default: '' },
  // Passed straight through to every OrgComponentVersionForm instance below —
  // see that component's own comment for why.
  componentName: { type: String, default: '' },
  componentLogoUrl: { type: String, default: '' },
  // True for the brief window right after the parent component was just
  // created — draws attention with a pulsing ring and opens the add form
  // straight away, since this is the first moment a version can be attached.
  highlight: { type: Boolean, default: false },
  // Optional — when given, that row gets an "Active" badge. Used by the
  // Applications factsheet page, which has a "currently active version"
  // concept on top of this shared version history; not used at all by the
  // IT Components page.
  currentVersionId: { type: String, default: '' },
})

const emit = defineEmits(['notify'])

const store = useOrgComponentVersionsStore()

// No org component to attach a version to yet (still creating one) — render
// an inert preview of the form instead of hitting the API.
const disabled = computed(() => !props.orgComponentId)

const entry = computed(() => store.entryFor(props.orgComponentId))
const versions = computed(() => entry.value.records)
const loading = computed(() => entry.value.loading)

// Read synchronously (a computed, not a ref, since orgComponentId can go
// from empty to real mid-lifecycle during the Add flow — see the
// orgComponentId watcher below) so the very first loading render already
// shows the right number of skeleton rows for this specific org_component,
// from a prior visit. Capped so a component with an unusually long version
// history can't render an absurdly tall skeleton.
const skeletonCount = computed(() => Math.min(getCachedOrgComponentVersionsCount(props.orgComponentId), 24))

const rootEl = ref(null)
const formOpen = ref(false)
const editingId = ref(null)
const saving = ref(false)
const formError = ref('')

// Lowercased version strings already tracked here — passed down so the
// form's catalog dropdown can flag a suggestion as "Already added" (mirrors
// AddItComponentSlideOver's IT Component field). The row currently being
// edited is excluded so its own unchanged version doesn't flag itself; for
// the Add row editingId is null, so nothing gets excluded there.
const existingVersionStrings = computed(() =>
  versions.value.filter((v) => v.id !== editingId.value).map((v) => v.version.toLowerCase()))

// Column order drives both the header row and the sortable fields — Alias
// and Actions render as plain (non-sortable) headers in their own slot here.
const COLUMNS = [
  { field: 'version', label: 'Version', sortable: true },
  { field: 'alias', label: 'Alias', sortable: false },
  { field: 'released_date', label: 'Released', sortable: true },
  { field: 'eol_date', label: 'End of life', sortable: true },
  { field: 'actions', label: 'Actions', sortable: false, align: 'right' },
]

// No default active sort — matches the previous plain list's order
// (whatever the API returned, created_at desc) until the user actually
// clicks a column. All sorting here runs client-side on the already-fetched
// `versions` array, no refetch.
const sortBy = ref(null)
const sortOrder = ref('desc')

function toggleSort(field) {
  if (sortBy.value === field) {
    sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortBy.value = field
    sortOrder.value = 'desc'
  }
}

// Dates: nulls (including eol_date_not_announced rows, whose eol_date is
// always cleared server-side) always sort last, regardless of direction.
function compareDates(a, b, dir) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  return (new Date(a) - new Date(b)) * dir
}

const sortedVersions = computed(() => {
  if (!sortBy.value) return versions.value

  const dir = sortOrder.value === 'asc' ? 1 : -1
  return [...versions.value].sort((a, b) => {
    switch (sortBy.value) {
      case 'released_date':
      case 'eol_date':
        return compareDates(a[sortBy.value], b[sortBy.value], dir)
      default:
        return a.version.localeCompare(b.version, undefined, { numeric: true, sensitivity: 'base' }) * dir
    }
  })
})

// Same convention as DependenciesSection's row tinting/badges (versionEolStatus.js
// is shared across both so thresholds/colors/wording never drift apart).
const ROW_BG_CLASSES = {
  none: '',
  healthy: 'bg-green-50/40 dark:bg-green-900/10',
  warning: 'bg-amber-50/40 dark:bg-amber-900/10',
  expired: 'bg-red-50/50 dark:bg-red-900/10',
}

function versionRowStatusBg(v) {
  if (v.eol_date_not_announced) return ROW_BG_CLASSES.healthy
  return ROW_BG_CLASSES[eolStatus(v.eol_date)]
}

function eolStatusMessage(eolDateValue) {
  const status = eolStatus(eolDateValue)
  if (status === 'expired') return 'End of life and support has ended for this version. An upgrade is recommended.'
  if (status === 'warning') return 'End of life is within the next 6 months. Plan an upgrade ahead of time.'
  if (status === 'healthy') return 'Supported for more than 6 months.'
  return ''
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function openForm() {
  editingId.value = null
  formError.value = ''
  formOpen.value = true
}

function openEditForm(v) {
  editingId.value = v.id
  formError.value = ''
  formOpen.value = true
}

function closeForm() {
  formOpen.value = false
  editingId.value = null
  formError.value = ''
}

async function submitVersion(payload) {
  saving.value = true
  formError.value = ''

  const res = editingId.value
    ? await store.update(props.organizationId, props.orgComponentId, editingId.value, payload)
    : await store.create(props.organizationId, props.orgComponentId, payload)

  saving.value = false

  if (!res.js_error && res.response.success) {
    const wasEditing = !!editingId.value
    closeForm()
    emit('notify', wasEditing ? 'Version updated.' : 'Version added.', 'notice')
  } else {
    const validationErrors = res.error?.data?.data
    formError.value = Array.isArray(validationErrors) && validationErrors.length
      ? validationErrors.join(', ')
      : 'Something went wrong. Please try again.'
  }
}

// Confirmation is required before a remove actually fires — mirrors
// DependenciesSection's pendingDetach, since Delete sits right next to Edit
// under the same hover reveal.
const pendingRemove = ref({ open: false, version: null, saving: false })

// Applications (currentVersionId is passed by the factsheet page) must
// always keep at least one version, and always have one selected as
// "current" — see OrgComponentApplication#current_version (required, no
// longer optional) and OrgComponentVersionsController#version_deletion_guard,
// which enforces the same rule server-side. IT-component-context usage
// (currentVersionId never passed) is unrestricted, same as before.
function canDelete(v) {
  if (!props.currentVersionId) return true
  if (versions.value.length <= 1) return false
  return v.id !== props.currentVersionId
}

function deleteDisabledReason(v) {
  if (canDelete(v)) return ''
  if (versions.value.length <= 1) return 'At least one version must remain.'
  return 'Switch to a different version before deleting the active one.'
}

function requestRemove(v) {
  if (!canDelete(v)) return
  pendingRemove.value = { open: true, version: v, saving: false }
}

function cancelRemove() {
  pendingRemove.value = { open: false, version: null, saving: false }
}

async function confirmRemove() {
  const v = pendingRemove.value.version
  if (!v) return

  pendingRemove.value.saving = true
  const res = await store.destroy(props.organizationId, props.orgComponentId, v.id)
  cancelRemove()

  if (!res.js_error && res.response.success) {
    emit('notify', 'Version removed.', 'notice')
  } else {
    emit('notify', 'Something went wrong. Please try again.', 'alert')
  }
}

function activate() {
  store.fetchIfNeeded(props.organizationId, props.orgComponentId)

  if (props.highlight) {
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    openForm()
  }
}

onMounted(() => {
  if (!disabled.value) activate()
})

// The parent now keeps this section mounted throughout the Add flow (so the
// disabled preview above can render), so orgComponentId can go from empty to
// a real id without a remount — pick that transition up here instead of
// relying on onMounted a second time.
watch(() => props.orgComponentId, (newId, oldId) => {
  if (newId && !oldId) activate()
})

// Lets a parent (the Applications factsheet page's VersionField "add a new
// version" footer action) open the add form on demand, without a highlight
// prop timing dance — the IT Components page never needs this.
defineExpose({ openForm })
</script>

<style scoped>
/* Mirrors OrgComponentsPanel.vue's own .card-highlight pulse — same ring +
   background fade, just on this section's rounded card instead of a grid
   item, so a just-created component's Versions area doesn't go unnoticed. */
.versions-highlight {
  animation: versions-highlight-pulse 3.5s ease-out;
}
@keyframes versions-highlight-pulse {
  0% {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.55);
    background-color: rgba(37, 99, 235, 0.07);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
    background-color: transparent;
  }
}
</style>
